import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as CloseIcon } from '../../images/close-2.svg';
import translation, { world } from '../../languages';
import helpCenter from '../../services/help-center';
import cn from '../../utils/classnames';
import isInIframe from '../../utils/is-in-iframe';
import isMainWorld from '../../utils/is-main-world';
import validator from '../../utils/validator';
import Button from '../button/button';
import { ReactComponent as FeedbackIcon } from './assets/feedback-icon.svg';
import { ReactComponent as SuccessFormIcon } from './assets/success-form-icon.svg';
import { FEEDBACK_STEPS } from './feedback-form.constants';
import './feedback-form.scss';

const FeedbackForm = ({ t }: TranslationProps) => {
    const user = useSelector((state: RootState) => state.main.user);
    const [formSteps, setFormSteps] = useState<FEEDBACK_STEPS>(FEEDBACK_STEPS.IDLE);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [description, setDescription] = useState('');
    const [email, setEmail] = useState('');
    const [isError, setIsError] = useState({ email: false, description: false });

    const handleOpenForm = () => setFormSteps(FEEDBACK_STEPS.FEEDBACK_FORM);
    const handleSuccess = () => setFormSteps(FEEDBACK_STEPS.SUCCESS);
    const handleClose = () => {
        setFormSteps(FEEDBACK_STEPS.IDLE);
        setIsError({ email: false, description: false });
        setErrorMessage(null);
        setDescription('');
        setEmail('');
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const feedbackBody = {
            topic: 'feedback',
            world,
            email: user?.email || email,
            description
        };

        if (description.length === 0 || !validator.validateEmail(feedbackBody.email)) {
            setIsError({ email: !validator.validateEmail(feedbackBody.email), description: description.length === 0 });

            return;
        }

        setErrorMessage(null);
        setIsError({ email: false, description: false });
        setIsLoading(true);
        try {
            await helpCenter.save(feedbackBody);
            handleSuccess();
        } catch {
            setErrorMessage(t('helpCenter.feedbackForm.failureText'));
        } finally {
            setIsLoading(false);
        }
    };

    const renderSteps = () => {
        switch (formSteps) {
            case FEEDBACK_STEPS.IDLE:
                return (
                    <div className='send-feedback-btn' onClick={handleOpenForm}>
                        <FeedbackIcon />
                        <span>{t('helpCenter.feedbackForm.feedbackBtnText')}</span>
                    </div>
                );
                break;
            case FEEDBACK_STEPS.FEEDBACK_FORM:
                return (
                    <div className='send-feedback-form'>
                        <div className='send-feedback-header' onClick={handleClose}>
                            <CloseIcon />
                        </div>
                        <form className='feedback-form' onSubmit={onSubmit}>
                            <h1 className='title'>{t('helpCenter.feedbackForm.title')}</h1>
                            {!user?.email ? (
                                <div className='form-group'>
                                    <span className='email'>{t('helpCenter.feedbackForm.emailText')}</span>
                                    <input
                                        className={cn('form-control', { 'field-error': isError.email })}
                                        placeholder={t('helpCenter.feedbackForm.emailPlaceholder')}
                                        type='email'
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>
                            ) : null}
                            <div className='form-group'>
                                <span className='description'>{t('helpCenter.feedbackForm.textareaText')}</span>
                                <textarea
                                    className={cn('form-control', { 'field-error': isError.description })}
                                    placeholder={t('helpCenter.feedbackForm.textareaPlaceholder')}
                                    rows={5}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </div>
                            {errorMessage && <span className='error-message'>{errorMessage}</span>}
                            <Button type='submit' variant='styled yellow feedback-button' loading={isLoading}>
                                {t('helpCenter.feedbackForm.btn')}
                            </Button>
                        </form>
                    </div>
                );
                break;
            case FEEDBACK_STEPS.SUCCESS:
                return (
                    <div className='send-feedback-form'>
                        <div className='send-feedback-header' onClick={handleClose}>
                            <CloseIcon />
                        </div>
                        <form className='feedback-form success' onSubmit={onSubmit}>
                            <SuccessFormIcon />
                            <div className='title'>{t('helpCenter.feedbackForm.successText')}</div>
                        </form>
                    </div>
                );
                break;
            default:
                return null;
        }
    };

    if (isMainWorld() || isInIframe()) {
        return null;
    }

    return <div className='feedback-wrapper'>{renderSteps()}</div>;
};

const translate = translation('help-center');

export default translate(FeedbackForm);
